<template>
  <Maintenance v-if="isMaintenance" />
  <div v-else class="app tw:flex tw:flex-1 tw:flex-col tw:h-screen tw:w-screen">
    <ToastController />
    <ModalController />
    <router-view v-if="noLayout" />
    <Layout
      v-else
      :key="authStore.getAuthId()"
      :collapsable="collapsable"
      :no-padding="noPaddingLayout"
    >
      <router-view v-slot="{ Component }">
        <transition
          name="fade"
          mode="out-in"
        >
          <component :is="Component" />
        </transition>
      </router-view>
    </Layout>
  </div>
</template>

<script lang="ts" setup>
import Maintenance from "@/components/common/MaintenancePage/MaintenancePage.vue";
import Layout from "@/layouts/AppLayout.vue";
import { computed, onMounted, onUnmounted, reactive } from "vue";
import { useStateStore } from "./store/state.store";
import ToastController from "@/layouts/ToastController.vue";
import ModalController from "./components/modals/_controller/ModalController.vue";
import { useRoute } from "vue-router";
import { useUIStore } from "./store/useUIStore";
import { useAuthStore } from "./store/auth.store";
import { useLayoutStore } from "./store/layout.store";
import { useAppStore } from "./store/app.store";

const COOKIE_BYPASS_NAME = "maintenanceBypass";

const state = reactive({
	loaded: false,
});

const route = useRoute();
useUIStore();
useStateStore();
const authStore = useAuthStore();
const layoutStore = useLayoutStore();
const appStore = useAppStore();

const noLayout = computed<boolean>(() => route.meta.noLayout as boolean);
const isMobileOrTablet = computed<boolean>(() => layoutStore.getLayout?.breakpoint <= 2);
const collapsable = computed<boolean>(() => !!(route.meta.sidebarCollapsable as boolean) || isMobileOrTablet.value);
const noPaddingLayout = computed<boolean>(() => route.meta.noPadding as boolean);

const isMaintenance = computed(() => {
	const isMaintenanceMode = import.meta.env.VUE_APP_MAINTENANCE === "true";
	const maintenanceBypass = import.meta.env.VUE_APP_MAINTENANCE_BYPASS;
	let cookieValue = "";

	const value = `; ${document.cookie}`;
	const parts = value.split(`; ${COOKIE_BYPASS_NAME}=`);
	if (parts.length === 2) cookieValue = parts.pop().split(";").shift();

	return cookieValue !== maintenanceBypass && isMaintenanceMode;
});

function handleResize() {
	layoutStore.actionSetMobile(window.innerWidth);
	layoutStore.actionRefreshLayout();
}

onMounted(async () => {
	try {
		handleResize();
		window.addEventListener("resize", handleResize);
		await appStore.actionLoad();
	} finally {
		state.loaded = true;
	}
});

onUnmounted(() => {
	window.removeEventListener("resize", handleResize);
});
</script>
